import React from 'react'
import { Element } from 'react-bulma-components'
import {
  WeekSummary,
  WeekSummaryEmployerRule,
} from '../../../../backend/src/services/resources/missions/missions.summary.service.types'
import { getDocIdAsString } from '../../utils/documents'
import { WorkPeriodWithSummary } from '../../api/work-periods'
import { DurationCell, EmployerRuleCell, HourCell } from '../summary/table/table-cells'
import { getWorkPeriodCellData, getWorkPeriodElementColor } from '../../utils/work-periods'
import { WorkPeriodSummaryDateTooltipContent } from './tooltips/work-period-summary-date-tooltip-content'
import { WorkPeriodSummaryBreaksTooltipContent } from './tooltips/work-period-summary-breaks-tooltip-content'
import {
  WorkPeriodAbandonedMessage,
  WorkPeriodEventsMessage,
  WorkPeriodTagsMessage,
} from './work-period-messages'

/**
 * Agnostic description of a summary work-period column that only needs the row's work period.
 * Use `minimalOnly` for fields hidden when the summary is in minimal mode (e.g. one day column).
 */
export interface WorkPeriodFieldDefinition {
  id: string
  header: string | React.ReactNode
  /** Renders cell content from the work period only */
  render: (workPeriod: WorkPeriodWithSummary) => React.ReactNode
  /** When true, the column is omitted in minimal summary mode */
  minimalOnly?: boolean
}

/**
 * Employer rule column: needs both the work period (for color) and a value from row totals.
 */
export interface EmployerRuleFieldDefinition {
  id: string
  header: string
  employerRule: WeekSummaryEmployerRule
  /**
   * Renders the cell: `ruleValue` comes from `totals.days[weekDay].employerData` in summary context,
   * or from the caller in a day-per-row table.
   */
  render: (workPeriod: WorkPeriodWithSummary | null, ruleValue: number | null) => React.ReactNode
}

/**
 * Options to control which optional columns are included in the work period fields.
 * @property showTheoreticalSchedule - When true, adds "Début Th." and "Fin Th." columns
 *   displaying the theoretical start/end from the work period summary checkpoint.
 *   Defaults to false (hidden).
 */
export interface WorkPeriodFieldOptions {
  showTheoreticalSchedule?: boolean
}

/**
 * Standard work-period summary columns: Début, Fin, Pause, Jour, Nuit, N.Effectif.
 * Optionally includes theoretical schedule columns (Début Th., Fin Th.) when
 * {@link WorkPeriodFieldOptions.showTheoreticalSchedule} is true.
 * Shared by the week summary day group and day-per-row tables.
 */
export const getWorkPeriodFields = (
  options: WorkPeriodFieldOptions = {},
): WorkPeriodFieldDefinition[] => [
  {
    id: 'start',
    header: 'Début',
    render: workPeriod => {
      const cellData = getWorkPeriodCellData(workPeriod, 'start')
      return (
        <HourCell
          date={cellData.date}
          textColor={cellData.color}
          tooltipId={cellData.tooltipId}
          tooltipContent={
            <WorkPeriodSummaryDateTooltipContent workPeriod={workPeriod} type="start" />
          }
        />
      )
    },
  },
  {
    id: 'end',
    header: 'Fin',
    render: workPeriod => {
      const cellData = getWorkPeriodCellData(workPeriod, 'end')
      return (
        <HourCell
          date={cellData.date}
          textColor={cellData.color}
          tooltipId={cellData.tooltipId}
          tooltipContent={
            <WorkPeriodSummaryDateTooltipContent workPeriod={workPeriod} type="end" />
          }
        />
      )
    },
  },
  ...(options.showTheoreticalSchedule
    ? ([
        {
          id: 'theoretical-start',
          header: 'Début Th.',
          minimalOnly: true,
          render: (workPeriod: WorkPeriodWithSummary) => (
            <HourCell date={workPeriod.summary.start.theoretical} />
          ),
        },
        {
          id: 'theoretical-end',
          header: 'Fin Th.',
          minimalOnly: true,
          render: (workPeriod: WorkPeriodWithSummary) => (
            <HourCell date={workPeriod.summary.end.theoretical} />
          ),
        },
      ] satisfies WorkPeriodFieldDefinition[])
    : []),
  {
    id: 'break',
    header: 'Pause',
    render: workPeriod => {
      const cellData = getWorkPeriodCellData(workPeriod, 'break')
      return (
        <DurationCell
          duration={workPeriod.summary.durations.breaks}
          tooltipContent={<WorkPeriodSummaryBreaksTooltipContent workPeriod={workPeriod} />}
          tooltipId={cellData.tooltipId}
          textColor={cellData.color}
        />
      )
    },
  },
  {
    id: 'day',
    header: 'Jour',
    minimalOnly: true,
    render: workPeriod => {
      const color = getWorkPeriodElementColor(workPeriod, 'other')
      return <DurationCell duration={workPeriod.summary.durations.worked.day} textColor={color} />
    },
  },
  {
    id: 'night',
    header: 'Nuit',
    minimalOnly: true,
    render: workPeriod => {
      const color = getWorkPeriodElementColor(workPeriod, 'other')
      return <DurationCell duration={workPeriod.summary.durations.worked.night} textColor={color} />
    },
  },
  {
    id: 'benefit',
    header: 'N.Effectif',
    minimalOnly: true,
    render: workPeriod => {
      const color = getWorkPeriodElementColor(workPeriod, 'event')
      return <DurationCell duration={workPeriod.summary.durations.benefit} textColor={color} />
    },
  },
]

/**
 * Meta columns shared by the summary day group and the day-per-row table: tags, interruption,
 * and events icons. Each cell only needs the work period row data.
 */
export const getWorkPeriodMetaFields = (): WorkPeriodFieldDefinition[] => [
  {
    id: 'tags',
    header: (
      // @ts-expect-error -- dummy work period for the column header icon only
      <WorkPeriodTagsMessage workPeriod={{ tags: [{}] }} type="icon" small text="Catégories" />
    ),
    minimalOnly: true,
    render: workPeriod => (
      <Element mt={1}>
        <WorkPeriodTagsMessage workPeriod={workPeriod} type="icon" small />
      </Element>
    ),
  },
  {
    id: 'interruption',
    minimalOnly: true,
    header: (
      <WorkPeriodAbandonedMessage
        workPeriod={{ isAbandoned: true }}
        type="icon"
        small
        iconColor="dark"
      />
    ),
    render: workPeriod => <WorkPeriodAbandonedMessage workPeriod={workPeriod} type="icon" small />,
  },
  {
    id: 'events',
    header: (
      <WorkPeriodEventsMessage
        workPeriod={{
          events: [
            {
              item: {
                type: 'benefit',
                _id: 'benefit',
                code: 'label',
                title: 'info',
                employer: '_id',
                indicatesAbsence: true,
              },
              comment: '',
            },
          ],
        }}
        text="Evènements"
        type="icon"
        small
        iconColor="dark"
      />
    ),
    render: workPeriod => {
      const color = getWorkPeriodElementColor(workPeriod, 'event')
      return (
        <Element mt={1}>
          <WorkPeriodEventsMessage workPeriod={workPeriod} type="icon" small iconColor={color} />
        </Element>
      )
    },
  },
]

/**
 * Builds one column definition per employer rule (summary display).
 * Callers supply `ruleValue` from their totals shape.
 */
export const getEmployerRuleFields = (
  employerRules: WeekSummary['employerRules'],
): EmployerRuleFieldDefinition[] =>
  employerRules.map(rule => ({
    id: `${rule.name}-${getDocIdAsString(rule.ruleId)}`,
    header: rule.shortName,
    employerRule: rule,
    render: (workPeriod, ruleValue) => (
      <EmployerRuleCell
        employerRule={rule}
        value={ruleValue}
        textColor={workPeriod ? getWorkPeriodElementColor(workPeriod, 'other') : ''}
      />
    ),
  }))
